import type { Ordre } from "types/ordre";

/** Le refus brut de ServiceBox, traduit en ce que l'équipe doit faire dans Carvis. */
function explain(error: string): string {
  const e = error.toLowerCase();
  if (e.includes("ccs indisponible"))
    return "Ce créneau est déjà pris dans ServiceBox pour ce réceptionnaire. Choisissez un autre créneau ou une autre colonne dans Carvis : il repartira tout seul vers ServiceBox.";
  if (e.includes("déjà en prêt") || e.includes("deja en pret"))
    return "La voiture de prêt est déjà prêtée sur cette période dans ServiceBox. Changez de voiture ou de dates dans Carvis.";
  if (e.includes("déjà été réceptionné") || e.includes("deja ete receptionne"))
    return "Le véhicule est déjà réceptionné dans ServiceBox : l'heure de réception n'y est plus modifiable.";
  if (e.includes("solution de mobilité") || e.includes("solution de mobilite"))
    return "Le prêt de ce RDV n'a pas de voiture dans ServiceBox.";
  if (e.includes("restitution trop courte"))
    return "ServiceBox juge la restitution trop proche de la réception pour la durée des interventions. Décalez la restitution (ou ajustez les durées) dans Carvis : il repartira tout seul.";
  if (e.includes("adresse e-mail valide"))
    return "ServiceBox refuse l'e-mail du client (caractères non autorisés). Corrigez-le sur la fiche client puis réenregistrez le RDV.";
  if (e.includes("parsing reponse"))
    return "ServiceBox a répondu de façon illisible. Réenregistrez le RDV pour relancer l'envoi.";
  if (e.includes("charge atelier"))
    return "ServiceBox refuse : la charge atelier ne permet pas ce RDV à cette date.";
  return error;
}

/**
 * Carvis est la référence, mais son dernier envoi vers ServiceBox a été refusé : les deux
 * ne montrent pas la même chose. Visible sur l'ordre (et « SB ≠ » sur le magnet) au lieu
 * d'être enfoui dans le menu Actions.
 */
export default function SbSyncBanner({ ordre, className = "" }: {
  ordre: Pick<Ordre, "sb_rdv_push_blocked" | "sb_status_push_blocked" | "sb_pretransfer_blocked"
    | "servicebox_rdv_id" | "sb_agenda_rdv_id">;
  className?: string;
}) {
  const rdv = ordre.sb_rdv_push_blocked?.error;
  const status = ordre.sb_status_push_blocked?.error;
  // RDV pris dans Carvis que ServiceBox n'a pas pu créer : MyO voit sa case vide.
  // Arrivé dans ServiceBox depuis (transfert manuel…) : l'ancien refus ne compte plus.
  const missing = ordre.servicebox_rdv_id || ordre.sb_agenda_rdv_id ? undefined : ordre.sb_pretransfer_blocked?.error;
  if (missing && !rdv && !status) {
    return (
      <div className={`flex flex-col gap-1 rounded-lg px-3 py-2 ${className}`}
        style={{ background: "#FFF7ED", border: "2px solid #EA580C", fontFamily: "Poppins" }} role="alert">
        <span className="text-h3 font-bold" style={{ color: "#9A3412" }}>
          Pas encore dans ServiceBox — dans MyO ce créneau paraît libre
        </span>
        <span className="text-h4" style={{ color: "#7C2D12" }}>{explain(missing)}</span>
      </div>
    );
  }
  const error = rdv || status;
  // Déjà réceptionné : ServiceBox a figé l'heure de réception, rien à faire.
  if (!error || /déjà été réceptionné/i.test(error)) return null;
  return (
    <div className={`flex flex-col gap-1 rounded-lg px-3 py-2 ${className}`}
      style={{ background: "#FFF7ED", border: "2px solid #EA580C", fontFamily: "Poppins" }} role="alert">
      <span className="text-h3 font-bold" style={{ color: "#9A3412" }}>
        Pas synchronisé avec ServiceBox — {rdv ? "le créneau" : "le statut"} n'y a pas été repris
      </span>
      <span className="text-h4" style={{ color: "#7C2D12" }}>{explain(error)}</span>
    </div>
  );
}
